import {LoadingDots} from '@commercial-helios/ui/src/components/loading-dots'
import {graphql} from 'gatsby'
import debounce from 'lodash/debounce'
import React from 'react'
import styled from 'styled-components'

import FeatureBlock from '@/components/feature-block'
import Button from '@helios/shared/components/button'
import {Section} from '@helios/shared/styles/grid'
import type ImageSource from '@helios/shared/types/image'
import {track, TrackingEvent} from '@helios/shared/utils/analytics'
import {showIntercom} from '@helios/shared/utils/intercom'

interface Props {
    title?: string
    body?: string
    ctaText?: string
    image?: ImageSource
}

enum ChatState {
    Enabled,
    Disabled,
    Loading,
}

// We tap into the isLauncherEnabled value of the intercom.intercom-state object in LocalStorage.
// This seems to be the most reliable point since it controls the behaviour of the Intercom widget.
const getLauncherState = () => {
    const isLauncherEnabled = JSON.parse(localStorage.getItem('intercom.intercom-state') || '{}')
        .launcher?.isLauncherEnabled

    if (isLauncherEnabled === undefined) {
        return ChatState.Loading
    }

    return isLauncherEnabled ? ChatState.Enabled : ChatState.Disabled
}

export const Chat = ({title, body, ctaText, image}: Props) => {
    const [state, setState] = React.useState<ChatState>(ChatState.Loading)

    React.useEffect(() => {
        const storageEventHandler = () => {
            setState(getLauncherState())
        }
        // The debounce addresses an issue with intercom-state where the isLauncherEnabled value
        // is toggled during the initial load of the page and causes the component to flicker.
        const dbStorageEventHandler = debounce(storageEventHandler, 500)

        const launcherState = getLauncherState()

        // If launcherState is loading we attach an event listener to the localStorage
        if (launcherState === ChatState.Loading) {
            window.addEventListener('storage', dbStorageEventHandler)
        } else {
            setState(launcherState)
        }

        return () => {
            window.removeEventListener('storage', dbStorageEventHandler)
        }
    }, [])

    // If we detect that the Intercom launcher is disabled then we don't render anything
    if (state === ChatState.Disabled) {
        return null
    }

    return (
        <StyledSection data-testid="chat-section">
            <VisibilityWrapper $visibility={state === ChatState.Enabled ? 'visible' : 'hidden'}>
                <FeatureBlock title={title} headingLevel="h2" body={body} image={image} mirrored>
                    {ctaText && (
                        <Button
                            data-testid="intercom-open-button"
                            variant="outline"
                            onClick={() => {
                                showIntercom()
                                track(TrackingEvent.ContactUsPageIntercomLinkActioned, {
                                    action: 'completed',
                                })
                            }}
                        >
                            {ctaText}
                        </Button>
                    )}
                </FeatureBlock>
            </VisibilityWrapper>
            {state === ChatState.Loading && <LoadingDots />}
        </StyledSection>
    )
}

const StyledSection = styled(Section)`
    position: relative;
`

interface VisibilityWrapperProps {
    $visibility: 'visible' | 'hidden'
}

const VisibilityWrapper = styled.div<VisibilityWrapperProps>`
    visibility: ${(props) => props.$visibility};
`

export const chatSliceFragment = graphql`
    fragment ChatSlice on PrismicSlicePageDataBodyChat {
        __typename
        id
        primary {
            title
            body1
            cta_text
            image {
                dimensions {
                    height
                    width
                }
                alt
                copyright
                url
                gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $twoColumnsAsymmetric)
            }
        }
    }
`
